import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 8.4 visual preview at /preview/lesson/8.4
 * "Picking the right tool for the job you actually have"
 * Categories of AI tools and a three-question fit test.
 * Category-only by design: brands change, categories are stable.
 */

export default function LessonPreviewPage8_4() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromPrev />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 8 · AI in a world where it has impacted everything · Lesson 8.4
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Picking the right tool for the job you actually have
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Categories · Fit test · Starter stack</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 fit-test drills · Brand names are on you to research
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Two people, same week. One has six AI subscriptions and is shipping nothing. The other has two and is shipping every day.">
      <p className="mt-6 text-text/90">
        Most people pick AI tools by hype. They see a launch video, sign up,
        try it twice, forget about it, and three months later they have six
        subscriptions, none of which match the work they actually do.
      </p>
      <p className="mt-4 text-text/90">
        Operators pick tools by fit. They start with the job they actually
        have. They figure out which category of tool that job lives in. They
        try one or two options inside the category. They keep the one that
        works for them and cancel the rest. The stack stays small. The work
        keeps shipping.
      </p>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Why this lesson stays category-level
        </p>
        <p className="mt-3 text-text/90">
          This lesson does not name specific tools, on purpose. Brands change
          fast. Categories are stable. If you learn the categories and the
          fit test, you can walk into any new product page and figure out in
          five minutes whether it belongs in your stack. The research is
          part of the practice. We are giving you the map, not picking the
          car for you.
        </p>
      </div>

      <p className="mt-6 text-text/90">
        Lesson 8.4 gives you five categories of AI tools, a three-question
        fit test, and the move for building a starter stack of one to three
        tools, not six.
      </p>
    </Section>
  );
}

/* --- Section 2: From 8.3 --- */
function FromPrev() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 8.3" title="8.3 was about keeping your judgment sharp. 8.4 is about picking the tools that judgment will use.">
      <p className="mt-6 text-text/90">
        In 8.3 you committed to the re-prove move: one task a week, no AI, on
        purpose. That practice only works if the rest of the time you are
        using AI well. Using AI well starts with picking the right tools, in
        the right number, for the work you actually do.
      </p>
      <p className="mt-4 text-text/90">
        Lesson 8.4 is the practical layer underneath the operator brief you
        will write in 8.5. By the end of this lesson you will have a draft
        of your starter stack: one to three tools, by category, with one
        sentence on what job each one does for you.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="One sentence on what drift is, and the re-prove move." />
        <ReviewItem n="02" q="Why a smaller AI stack tends to ship more than a bigger one." />
        <ReviewItem n="03" q="One job you do every week that AI could plausibly help with." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · Categories, the fit test, and your stack" title="Five tool categories worth knowing. A three-question fit test. A stack of one to three, not six.">
      <Subhead n={1} title="The vocab for the lesson" />
      <VocabCard
        term="Fit"
        definition="Fit is the match between a tool and the job you actually have. A tool fits when it does a real job in your work meaningfully better than what you do now, you can use it today, and you could re-prove the underlying work without it if you had to. Fit is the opposite of buying tools because they are popular."
        analogy="Fit is shopping for shoes for a sport you actually play. Plenty of people own running shoes who do not run. They look the part on the shelf. They never improve the runner&apos;s mile time, because there is no runner. AI tools work the same way. A coding assistant for someone who does not code is a running shoe in a closet. Fit asks: are you the runner. Then it asks: does this shoe help you run."
        memoryHook="A tool fits when it makes a real job easier, you can use it today, and you would not be helpless without it. If any of those three are missing, it is not fit yet."
        why="Operators with small, well-fit stacks ship more than operators with large, poorly-fit ones. Fit is also how you stay calm when a new tool launches. You look at it through the lens of fit, decide quickly, and move on. Without fit, every announcement is a tax on your attention and your wallet."
      />

      <Subhead n={2} title="Five categories of AI tools worth knowing" className="mt-12" />
      <p className="mt-4 text-text/90">
        These are the categories the field has settled into so far. The
        names will keep shifting at the edges, but the shape of the work
        each category does is stable. We are not naming brands. Your job is
        to pick a category that matches your work, then go research the two
        or three current options inside it.
      </p>
      <CategoriesGrid />

      <Subhead n={3} title="The fit test, in three questions" className="mt-12" />
      <p className="mt-4 text-text/90">
        Before adopting any AI tool, ask these three questions. Two yes and
        one no usually means &ldquo;interesting, not for you yet.&rdquo;
        Three yes means run a small trial. Anything less, skip.
      </p>
      <FitTestGrid />

      <Subhead n={4} title="Where AI helps and where AI hurts on tool picking" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is useful for surveying a category and short-listing options. It
        is poor at telling you whether a tool fits your specific work, your
        specific anchors, your specific budget.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The starter stack: one to three, not six" className="mt-12" />
      <p className="mt-4 text-text/90">
        A starter stack has one to three tools. Pick the categories that
        match your two or three biggest jobs. For most students and recent
        grads, a stack looks like one chat or research tool, plus one
        category-specific tool tied to a real project. That is it. Add a
        third only when a new job emerges, not because something looks
        shiny.
      </p>
      <StarterExamples />

      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          The rule
        </p>
        <p className="mt-3 text-text/90">
          Smaller stack, real jobs, named fit. If you cannot say in one
          sentence what job each tool does, the stack is too big and you
          probably do not know what you are doing with half of it.
        </p>
      </div>
    </Section>
  );
}

const CATEGORIES = [
  {
    n: '01',
    name: 'Chat & research',
    body: 'General-purpose thinking partners. Drafting, summarizing, walking through a problem, doing fast research, comparing things, restructuring a doc. The widest category and usually the first one operators adopt.',
    jobs: [
      'Draft a first version of a brief, memo, or post you will then own and edit.',
      'Get a fast lay of the land on a topic before you read the actual sources.',
      'Restructure a tangled paragraph or outline.',
      'Pressure-test your own argument before you send it.',
    ],
  },
  {
    n: '02',
    name: 'Coding',
    body: 'Tools that help you write, edit, refactor, and run software. Includes editor assistants, agentic coding tools, browser-based code environments, and prototyping tools that turn a description into a working app. Useful only if “writing software” is a job you actually have.',
    jobs: [
      'Build a small working prototype for a class project, a club app, or a side project.',
      'Get unstuck on a bug you would have asked a senior engineer about.',
      'Refactor or rewrite code in a language you know well.',
      'Spin up an internal tool or script that saves you hours of repeat work.',
    ],
  },
  {
    n: '03',
    name: 'Voice & audio',
    body: 'Tools that turn speech into text and text into speech. Transcription of meetings and lectures, voice-driven AI chat, note dictation, and audio generation. The category that grew the most in the last year and is the easiest to underestimate.',
    jobs: [
      'Capture meetings or interviews as searchable, structured notes.',
      'Dictate a long email or memo while walking instead of typing it cold.',
      'Talk through a problem with AI when you think better out loud than on a keyboard.',
      'Generate clean audio for a class project or a small media piece you are producing.',
    ],
  },
  {
    n: '04',
    name: 'Image & video',
    body: 'Tools that generate or edit visuals. Static images for documents, decks, and posts. Short-form video for content, prototypes, or storytelling. Includes editing tools that use AI to remove backgrounds, retouch photos, or upscale footage.',
    jobs: [
      'Generate a hero image for a post, deck, or small site instead of pulling stock.',
      'Make a quick visual that supports an idea in a brief or a class presentation.',
      'Edit, retouch, or clean up real media you already have.',
      'Produce a short clip for a project where a wall of text would be the wrong move.',
    ],
  },
  {
    n: '05',
    name: 'Orchestration & agents',
    body: 'Tools that chain AI into multi-step workflows that run on their own. Includes automation platforms, custom agents that take actions on the web or in your apps, and browser-driving AI. The most powerful category and the one with the most ways to over-engineer a solution to a problem you do not have.',
    jobs: [
      'Auto-process incoming forms, leads, or applications into the right system.',
      'Build a recurring research or summary task that runs without you each week.',
      'Connect two tools that do not talk to each other, with AI in the middle making decisions.',
      'Run a long, multi-step task in the background (research, scraping, drafting) while you work on something else.',
    ],
  },
];

function CategoriesGrid() {
  return (
    <div className="mt-6 space-y-4">
      {CATEGORIES.map((c) => (
        <div key={c.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">Category {c.n}</span>
            <h4 className="text-lg font-semibold text-amber">{c.name}</h4>
          </div>
          <p
            className="mt-3 text-text/90"
            dangerouslySetInnerHTML={{ __html: c.body }}
          />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-wider text-muted">
            Real jobs this category does
          </p>
          <ul className="mt-2 space-y-1.5">
            {c.jobs.map((j, i) => (
              <li key={i} className="flex gap-2 text-sm text-text/85">
                <span className="shrink-0 text-amber">·</span>
                <span>{j}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

const FIT_TESTS = [
  {
    n: '01',
    q: 'What job does this do that I am currently doing badly or slowly?',
    body: 'If you cannot name the job in one sentence, do not adopt the tool. “Looks cool” is not a job. “I currently spend four hours a week on meeting notes” is a job. Tools without a named job become tabs you forget to close.',
  },
  {
    n: '02',
    q: 'What anchor does this touch?',
    body: 'If the tool touches mechanical work (cleanup, formatting, draft generation), the bar is lower. If it touches judgment, standing, or relationship (from 8.2), the bar is higher. A tool that wants to send messages for you needs to clear a much higher bar than a tool that helps you take notes.',
  },
  {
    n: '03',
    q: 'Could I re-prove the work without it?',
    body: 'If you have to answer no, you are not buying a tool, you are buying a dependency. Sometimes that is the right call. More often it is not. The safest tools to adopt are the ones that make work faster but do not change whether you can do the work at all.',
  },
];

function FitTestGrid() {
  return (
    <div className="mt-6 space-y-4">
      {FIT_TESTS.map((t) => (
        <div key={t.n} className="rounded-lg border border-line bg-surface p-5">
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-xs text-amber">Q{t.n}</span>
            <h4
              className="text-lg font-semibold text-text"
              dangerouslySetInnerHTML={{ __html: t.q }}
            />
          </div>
          <p
            className="mt-3 text-sm text-text/90"
            dangerouslySetInnerHTML={{ __html: t.body }}
          />
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Surveying a category fast: give it your job and ask what category and what kinds of tools live there.',
      'Generating short-lists you can then research yourself before signing up.',
      'Comparing two options on a specific job you describe, not on generic features.',
      'Drafting your one-sentence rationale for each tool in your stack.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'Picking the tool: the chatbot will not know your real work, your budget, or your taste.',
      'Generating fear of missing out: ask it about new tools and you will end up with twelve tabs open.',
      'Sneaking new categories into your stack: every “you might also like” is a stack-creep risk.',
      'Replacing the trial: a tool only really passes the fit test on real work, not in a demo.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span dangerouslySetInnerHTML={{ __html: it }} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

const STARTER_STACKS = [
  {
    who: 'High schooler with one big creative project',
    stack: [
      { cat: 'Chat & research', job: 'Help me think through arguments and structure my pieces before I write.' },
      { cat: 'Image & video', job: 'Generate one hero visual per post or project, the rest is words.' },
    ],
  },
  {
    who: 'College student with a coding-heavy capstone',
    stack: [
      { cat: 'Chat & research', job: 'Brainstorm and pressure-test the project direction; draft non-code writing.' },
      { cat: 'Coding', job: 'Pair-program on the codebase, get unstuck, refactor messy sections.' },
    ],
  },
  {
    who: 'Recent grad on a busy operations team',
    stack: [
      { cat: 'Chat & research', job: 'Draft, summarize, and pressure-test the work I owe my manager every week.' },
      { cat: 'Voice & audio', job: 'Capture meetings and 1:1s as searchable notes I can actually find later.' },
      { cat: 'Orchestration & agents', job: 'One recurring workflow that turns weekly inputs into a clean report.' },
    ],
  },
];

function StarterExamples() {
  return (
    <div className="mt-6 space-y-4">
      {STARTER_STACKS.map((s) => (
        <div key={s.who} className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            {s.who}
          </p>
          <ul className="mt-3 space-y-2">
            {s.stack.map((row, i) => (
              <li key={i} className="grid gap-1 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-3">
                <span className="font-mono text-[11px] uppercase tracking-wider text-amber">
                  {row.cat}
                </span>
                <span className="text-sm text-text/90">{row.job}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Run the fit test" title="Three real situations. Each one names a job. Pick the category that fits the job, not the one that sounds the most impressive.">
      <p className="mt-6 text-text/90">
        Each scene gives you a job. Two categories could plausibly help. One
        is the better fit given what the person actually has to do. Pick
        that one.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <FitDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  scene: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    scene:
      'You are editing the high school newspaper. You spend four hours every week transcribing interview audio into text, then another two hours pulling quotes. You write all of the actual articles yourself and are proud of your voice.',
    optionA: 'Chat & research: have AI draft the articles to save time.',
    optionB: 'Voice & audio: transcription, so the four hours of typing goes away.',
    correct: 'B',
    verdict:
      'The job in front of you is transcription, not writing. Voice & audio is the category that maps directly to the work that is eating your time. Chat & research is tempting, but the writing is exactly the part you want to keep doing, and outsourcing it would erode the voice you are proud of. The fit move is to keep your writing yours and let the AI take the mechanical hours back.',
  },
  {
    n: '02',
    scene:
      'You are leading a college capstone. Your team gets twenty-plus applications a week from underclassmen wanting to join the project. Each one requires a short screening form, a quick conversation, and a decision logged in your shared sheet. The grunt work is killing you.',
    optionA: 'Orchestration & agents: build a workflow that handles intake and routes only real candidates to a human.',
    optionB: 'Chat & research: ask AI to summarize each application as it comes in.',
    correct: 'A',
    verdict:
      'Summarizing applications one by one still leaves you doing all the orchestration: receive, log, schedule, decide. The job in the scene is a recurring multi-step process across applications, intake, and a shared sheet. That is exactly what orchestration is for. The fit move is to design a small workflow that catches obvious no-fits before they reach you and routes real candidates with a clean intake. Then your human time goes only to the decisions that matter.',
  },
  {
    n: '03',
    scene:
      'You are a recent grad on a marketing team. You write three to five short LinkedIn posts a week for your senior leader. The biggest pain is not writing, it is generating a custom image for each post that does not look like every other stock photo.',
    optionA: 'Image & video: generate a tailored visual per post in your team\'s style.',
    optionB: 'Coding: build a custom internal tool that grabs trending posts.',
    correct: 'A',
    verdict:
      'The named job is making post images that do not look like everyone else\'s stock photos. That is squarely image & video. The coding option might be interesting eventually, but it is a different job, not the one in front of you. The fit move is to spend a week trying image-generation tools on real posts, decide which one matches your team\'s style, and stop there. Do not let the cool-tool itch sneak a fifth category into a three-tool stack.',
  },
];

function FitDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The scene
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.scene }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which category actually fits the job?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span className="mt-2 block" dangerouslySetInnerHTML={{ __html: text }} />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That category could help on some job, but it is not the job
              in the scene. Pick the category that matches the specific
              task eating the person&apos;s time today.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The category that actually fits' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90" dangerouslySetInnerHTML={{ __html: drill.verdict }} />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Fit in one sentence." answer="The match between a tool and the job you actually have: it does a real job better than what you do now, you can use it today, and you could re-prove the underlying work without it." />
        <QuickCheckItem n="Q2" q="The five tool categories." answer="Chat &amp; research, coding, voice &amp; audio, image &amp; video, orchestration &amp; agents." />
        <QuickCheckItem n="Q3" q="The three fit-test questions." answer="What job does this do that I am doing badly or slowly? What anchor does it touch? Could I re-prove the work without it?" />
        <QuickCheckItem n="Q4" q="Why this lesson does not name specific brands." answer="Brands change fast. Categories are stable. Learning categories plus the fit test means you can evaluate any new tool yourself in five minutes." />
        <QuickCheckItem n="Q5" q="What is a starter stack?" answer="One to three tools, by category, with one sentence each on the real job they do. Smaller stack, real jobs, named fit." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. Fit, categories, the stack.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 8.4 · Lock card · Fit
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Pick categories, not brands. Match tools to jobs you actually
          have. Keep the stack small enough to defend.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Chat" body="General thinking partner. Draft, summarize, restructure." />
          <LockTile n="02" name="Coding" body="Write, edit, refactor, prototype software." />
          <LockTile n="03" name="Voice" body="Transcription, dictation, talking through ideas." />
          <LockTile n="04" name="Image / video" body="Visuals for real work, not for the feed." />
          <LockTile n="05" name="Orchestration" body="Chains of AI that run without you." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The fit test
          </p>
          <p className="mt-2 text-text/90">
            What real job does this do? What anchor does it touch? Could I
            re-prove the work without it? Three yes, run a trial. Anything
            less, skip.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The starter stack
          </p>
          <p className="mt-2 text-text/90">
            One to three tools, by category, with one sentence each on the
            real job they do. Add a fourth only when a new job emerges.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Tools are not the operator. You are. The stack is just the gear
          you carry.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 8.5 · Week 8 capstone
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The operator: your first 90 days after this program
          </h3>
          <p className="mt-3 text-sm text-text/85">
            The close. Vocab: Operator. You will write a one-page operator
            brief: where you are now, your three anchors, your weekly AI
            rhythm, your starter stack, your re-prove ritual, your team
            standard, your learning loop. Eight sections. One page. Ships
            today.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 8.4 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90" dangerouslySetInnerHTML={{ __html: body }} />
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 8.3',
    'Categories & fit',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
